import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { http, formatApiError } from "../api";

/**
 * Checkout modal — replaces the old waitlist modal.
 *
 * One global instance lives inside <UpgradeProvider>. Any "Upgrade to Navigator"
 * CTA calls useUpgrade().open(featureKey, context); the provider opens this
 * modal with the right copy. Clicking "Continue to checkout" creates a Stripe
 * Checkout Session via /api/stripe/checkout-session and redirects to it.
 *
 * The success_url/cancel_url come back to /app/billing with a query flag so
 * Billing.js can show the right confirmation/cancel state.
 */
export default function CheckoutModal({
  open,
  onClose,
  feature,
  context,
  headline,
  subhead,
  valueProps = [],
}) {
  const { user } = useAuth();
  const [billing, setBilling] = useState("annual");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setBilling("annual");
      setBusy(false);
      setErr("");
    }
  }, [open]);

  if (!open) return null;

  const price = billing === "annual" ? "$11" : "$14.99";
  const billingNote =
    billing === "annual"
      ? "billed $132 annually · save $48"
      : "billed monthly · cancel anytime";

  const continueToCheckout = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await http.post("/stripe/checkout-session", {
        period: billing,
        origin_url: window.location.origin,
        feature: feature || "",
        context: context || "",
      });
      if (!data?.url) throw new Error("No checkout URL returned.");
      // Fire analytics non-blockingly
      try {
        http.post("/analytics/event", {
          event: "upgrade_checkout_clicked",
          properties: { period: billing, feature: feature || "", context: context || "" },
        }).catch(() => {});
      } catch (e) { /* ignore */ }
      // Hand off to Stripe-hosted checkout
      window.location.href = data.url;
    } catch (e2) {
      setErr(formatApiError(e2));
      setBusy(false);
    }
  };

  return (
    <div
      className="upgrade-modal-overlay"
      data-testid="checkout-modal"
      onClick={() => onClose("backdrop")}
    >
      <div className="upgrade-modal-card" onClick={(e) => e.stopPropagation()}>
        <button
          className="upgrade-modal-close"
          onClick={() => onClose("close-btn")}
          aria-label="Close checkout"
          data-testid="checkout-modal-close"
        >
          ×
        </button>

        <div className="upgrade-modal-eyebrow">Navigator · monthly or annual</div>
        <h2 className="upgrade-modal-headline" data-testid="checkout-modal-headline">
          {headline}
        </h2>
        <p className="upgrade-modal-subhead">{subhead}</p>

        <ul className="upgrade-modal-perks">
          {valueProps.slice(0, 4).map((v, i) => (
            <li key={i}>
              <span className="upgrade-tick" aria-hidden>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <path d="M5 12l5 5L20 7" />
                </svg>
              </span>
              {v}
            </li>
          ))}
        </ul>

        <div className="upgrade-billing-row">
          <div className="upgrade-billing-toggle" role="tablist" aria-label="Billing cycle">
            <button
              type="button"
              className={`u-toggle ${billing === "monthly" ? "active" : ""}`}
              onClick={() => setBilling("monthly")}
              data-testid="checkout-billing-monthly"
            >
              Monthly
            </button>
            <button
              type="button"
              className={`u-toggle ${billing === "annual" ? "active" : ""}`}
              onClick={() => setBilling("annual")}
              data-testid="checkout-billing-annual"
            >
              Annual <span className="save-pill">save $48</span>
            </button>
          </div>
          <div className="upgrade-price">
            <span className="upgrade-amount" data-testid="checkout-price-amount">{price}</span>
            <span className="upgrade-period">/{billing === "annual" ? "mo" : "month"}</span>
          </div>
        </div>
        <div className="upgrade-billing-note" data-testid="checkout-billing-note">{billingNote}</div>

        {err && <div className="upgrade-error" data-testid="checkout-error">{err}</div>}

        <div className="upgrade-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => onClose("maybe-later")}
            data-testid="checkout-maybe-later"
          >
            Maybe later
          </button>
          <button
            type="button"
            className="btn btn-clay btn-primary"
            disabled={busy}
            onClick={continueToCheckout}
            data-testid="checkout-continue-btn"
          >
            {busy ? "Redirecting…" : "Continue to checkout"}
          </button>
        </div>
        <p className="upgrade-fineprint">
          Secure checkout by Stripe · 7-day money-back · Cancel anytime
        </p>
        <p className="upgrade-fineprint" data-testid="checkout-country-disclaimer" style={{ marginTop: 4 }}>
          Not in a <a href="https://stripe.com/global" target="_blank" rel="noopener noreferrer">Stripe-supported country</a>? Email{" "}
          <a href="mailto:hello@govidhya.com?subject=Upgrade%20my%20account">hello@govidhya.com</a> to upgrade your account.
        </p>
        {user?.email && (
          <p className="upgrade-fineprint" style={{ marginTop: 2 }}>
            Charging <strong>{user.email}</strong>
          </p>
        )}
      </div>
    </div>
  );
}
